<template>
  <div class="d-flex flex-column">
    <div class="row legends justify-content-md-center">
      <slot name="legend" :updateDataset="updateDataset"></slot>
    </div>
    <div class="wrapper_line_chart">
      <canvas
          hide-selected="true"
          tabindex="1"
          class="chart"
          id="LineWithLine"
          ref="canvas"
      ></canvas>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { shallowRef } from 'vue'

import { mapGetters, mapActions } from 'vuex'
import nFormatter from '@/helpers/processing/nFormatter'
import getColor from '@/helpers/store/getColor'
import isMobile from '@/helpers/base/isMobile'
import getFormattedDate from '@/helpers/date/getFormattedDate'
import { LineCustom } from './LineCustom'
import buildTooltip from './buildTooltip'
import attachTouchCrosshair from './touchCrosshair'
import { cssVar, onThemeChange } from '@/helpers/base/theme'

Chart.register(LineCustom)
Chart.defaults.font = {
  family: 'Manrope',
  size: 12,
  style: 'normal',
}

export default {
  data() {
    const self = this
    return {
      removeLoading: false,
      chartLegend: [],
      chartOptions: {
        layout: {
          padding: 0,
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: 'index',
            cornerRadius: 7,
            intersect: false,
            enabled: false,
            external(context) {
              buildTooltip.bind(this, context, self.$t, self.$tc)()
            },
            bodySpacing: 7,
            padding: 20,
            position: 'nearest',
            borderColor: cssVar('--border-color-2'),
            borderWidth: 1,
            backgroundColor: cssVar('--bg-surface'),
          },
        },
        events: ['mousemove', 'mouseout', 'click'],
        // на мобильном по тапу подсвечиваем точки всех линий в колонке крестика,
        // как при драге; по умолчанию hover ищет пересечение с точкой, а точки тут радиуса 0
        ...(this.isMobile() ? { hover: { mode: 'index', intersect: false } } : {}),
        aspectRatio: this.isMobile() ? 1.1 : 2.2,
        animation: false,
        scales: {
          x: {
            ticks: {
              color: cssVar('--chart-tick'),
              padding: 0,
              tickLength: 0,
              // на мобильном подписи месяцев чуть левее своей точки
              labelOffset: this.isMobile() ? -4 : 0,
              font: { size: this.isMobile() ? 10 : 12 },
              callback: (value, index, values) => {
                return getFormattedDate(this.chartData.labels[value], {
                  month: 'short',
                })
              },
            },
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: cssVar('--chart-tick'),
              padding: this.isMobile() ? 4 : 15,
              font: { size: this.isMobile() ? 10 : 12 },
              callback: (value, index, values) => {
                return this.nFormatter(value)
              },
            },
            grid: {
              borderDash: [7, 7],
              tickLength: 0,
              color: cssVar('--chart-grid'),
            },
          },
        },
        elements: {
          point: {
            radius:
                this.isMobile() || this.options?.activePeriod === 'all' ? 0 : 2,
            borderWidth: 5,
          },
          line: {
            tension: 0.3,
            borderWidth: 3,
          },
        },
      },
    }
  },
  props: ['chartData', 'options'],
  components: {},
  mounted() {
    const config = {
      type: 'LineWithLine',
      data: this.chartData,
      options: this.chartOptions,
    }
    this.chart = shallowRef(new Chart(this.$refs.canvas, config))
    // на мобильных крестик ведётся пальцем (драг / удержание), см. touchCrosshair.js
    this.detachTouchCrosshair = attachTouchCrosshair(this.chart.value)
    // при смене темы перекрашиваем сетку и подписи осей
    this.detachThemeListener = onThemeChange(() => this.applyThemeColors())
  },
  beforeUnmount() {
    if (this.detachTouchCrosshair) this.detachTouchCrosshair()
    if (this.detachThemeListener) this.detachThemeListener()
    // тултип — общий элемент в body, прячем, чтобы не остался от удалённого графика
    const tooltipEl = document.getElementById('chartjs-tooltip')
    if (tooltipEl) tooltipEl.style.opacity = 0
    if (this.chart && this.chart.value) this.chart.value.destroy()
  },
  methods: {
    ...mapActions(['removeDomain']),
    nFormatter,
    getColor,
    buildTooltip,
    isMobile,
    updateDataset(index) {
      if (!this.chart || !this.chart.value) return

      const chart = this.chart.value
      // Проверяем, что датасет существует
      if (!chart.data.datasets[index]) {
        console.warn(`Dataset with index ${index} not found`)
        return
      }

      // Используем встроенный метод Chart.js для переключения видимости
      const isVisible = chart.isDatasetVisible(index)
      if (isVisible) {
        chart.hide(index)
      } else {
        chart.show(index)
      }
    },
    applyThemeColors() {
      const chart = this.chart && this.chart.value
      if (!chart) return
      const { scales, plugins } = chart.options
      scales.x.ticks.color = cssVar('--chart-tick')
      scales.y.ticks.color = cssVar('--chart-tick')
      scales.y.grid.color = cssVar('--chart-grid')
      plugins.tooltip.borderColor = cssVar('--border-color-2')
      plugins.tooltip.backgroundColor = cssVar('--bg-surface')
      chart.update('none')
    },
    refreshChart() {
      // Обновляем данные графика
      if (this.chart && this.chart.value) {
        this.chart.value.data.datasets = this.chartData.datasets
        this.chart.value.update('none') // 'none' - без анимации для мгновенного обновления
      }
    },
  },
}
</script>
<style scoped>
.legends {
  margin: 0 0 10px 0;
  padding: 0px 15px;
}

@media (max-width: 767px) {
  .legends {
    padding: 0 2px;
  }
  /* график шире: заходит на внутренние отступы карточки */
  .wrapper_line_chart {
    margin: 0 -7px;
  }
}
#LineWithLine:focus {
  outline: none;
}

.wrapper_input {
  align-items: center;
  margin-right: 20px;
  margin-bottom: 15px;
}
.wrapper_input:last-child {
  margin-right: 0px;
}

.wrapper_line_chart {
  position: relative;
  max-height: 548px;
}

canvas {
  position: relative;
  z-index: 3;
  /* вертикальный свайп по графику — скролл страницы браузером,
     горизонтальный — наш, ведёт крестик (touchCrosshair.js) */
  touch-action: pan-y pinch-zoom;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}
</style>
